import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, apiError } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../i18n";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export default function RegisterClinic() {
  const { t } = useI18n();
  const { loginSuccess } = useAuth();
  const nav = useNavigate();
  const [showPwd, setShowPwd] = useState(false);
  const [f, setF] = useState({
    clinic_name: "",
    country: "SN",
    currency: "XOF",
    owner_first_name: "",
    owner_last_name: "",
    owner_email: "",
    owner_password: "",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (f.owner_password.length < 6) {
      toast.error(t("common.required") + ": min 6");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register-clinic", f);
      loginSuccess(data);
      toast.success(t("common.success"));
      nav("/app");
    } catch (err) {
      toast.error(apiError(err, t("common.error")));
    } finally {
      setLoading(false);
    }
  };

  const input = "w-full rounded-xl border border-[var(--border-default)] bg-white px-4 h-14 text-base focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none";

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:flex bg-brand overflow-hidden">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage: `url("https://images.pexels.com/photos/5234519/pexels-photo-5234519.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-brand/80 via-brand/60 to-brand/90" />
        <div className="relative z-10 p-12 text-white flex flex-col justify-center">
          <div className="flex items-center gap-3 mb-10">
            <img
              src="/logo.png"
              alt="MediFlow Africa"
              className="w-14 h-14 rounded-2xl object-cover bg-[#3a3a3a] shadow-lg"
            />
            <span className="font-heading text-xl font-bold">MediFlow Africa</span>
          </div>
          <h2 className="font-heading text-4xl font-extrabold leading-tight max-w-md">
            Démarrez votre clinique en quelques minutes.
          </h2>
          <ul className="mt-8 space-y-3 text-white/85">
            <li>✓ Multi-utilisateurs, multi-praticiens, multi-spécialités</li>
            <li>✓ Stock médical avec lots & péremptions</li>
            <li>✓ Paiements cash / Wave / Orange Money</li>
            <li>✓ Isolation stricte des données par clinique</li>
          </ul>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-[var(--bg-default)]">
        <div className="w-full max-w-lg">
          <h1 className="font-heading text-3xl font-extrabold text-ink">{t("auth.createClinic")}</h1>
          <p className="mt-2 text-ink-muted">{t("app.tagline")}</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="block mb-2 text-sm font-semibold text-ink">{t("auth.clinicName")}</label>
              <input
                required value={f.clinic_name}
                onChange={(e) => setF({ ...f, clinic_name: e.target.value })}
                data-testid="reg-clinic-name"
                className={input}
                placeholder="Clinique Le Baobab"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block mb-2 text-sm font-semibold text-ink">{t("auth.ownerFirstName")}</label>
                <input
                  required value={f.owner_first_name}
                  onChange={(e) => setF({ ...f, owner_first_name: e.target.value })}
                  data-testid="reg-first-name"
                  className={input}
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-semibold text-ink">{t("auth.ownerLastName")}</label>
                <input
                  required value={f.owner_last_name}
                  onChange={(e) => setF({ ...f, owner_last_name: e.target.value })}
                  data-testid="reg-last-name"
                  className={input}
                />
              </div>
            </div>
            <div>
              <label className="block mb-2 text-sm font-semibold text-ink">{t("auth.email")}</label>
              <input
                type="email" required value={f.owner_email}
                onChange={(e) => setF({ ...f, owner_email: e.target.value })}
                data-testid="reg-email"
                className={input}
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-semibold text-ink">{t("auth.password")}</label>
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"} required value={f.owner_password}
                  onChange={(e) => setF({ ...f, owner_password: e.target.value })}
                  data-testid="reg-password"
                  className={`${input} pr-12`}
                  placeholder="min. 6 caractères"
                />
                <button type="button" onClick={() => setShowPwd((v) => !v)} data-testid="reg-password-toggle"
                  aria-label={showPwd ? "Masquer" : "Afficher"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg text-ink-muted hover:text-brand hover:bg-subtle">
                  {showPwd ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block mb-2 text-sm font-semibold text-ink">{t("auth.country")}</label>
                <input value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} className={input} />
              </div>
              <div>
                <label className="block mb-2 text-sm font-semibold text-ink">{t("auth.currency")}</label>
                <input value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })} className={input} />
              </div>
            </div>

            <button
              type="submit" disabled={loading}
              data-testid="reg-submit"
              className="w-full h-14 rounded-xl bg-brand hover:bg-brand-hover text-white font-heading font-bold text-base transition-colors disabled:opacity-60 active:scale-[0.98]"
            >
              {loading ? t("common.loading") : t("auth.createClinic")}
            </button>

            <div className="text-sm text-center">
              <Link to="/login" className="text-brand font-semibold hover:underline" data-testid="link-login">
                {t("auth.hasAccount")}
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
